import React, { useState, useEffect } from 'react';
import { CheckoutData } from '../../pages/checkout/HostingCheckout';
import { domainService, DomainAvailability } from '../../services/domainService';

interface DomainStepProps {
  data: CheckoutData;
  onNext: (data: Partial<CheckoutData>) => void;
  onBack: () => void;
  /**
   * Which checkout is embedding this step. Business Email can't perform a
   * registrar transfer yet (it was billed but never executed), so the
   * transfer option is hidden for `product="email"`. Hosting keeps it.
   */
  product?: 'hosting' | 'email';
}

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

const DomainStep: React.FC<DomainStepProps> = ({ data, onNext, onBack, product = 'hosting' }) => {
  const isEmail = product === 'email';
  const [domainName, setDomainName] = useState(data.domainName);
  const [domainAction, setDomainAction] = useState<'register' | 'transfer' | 'existing'>(
    // Transfer isn't offered for email — coerce a stale selection.
    isEmail && data.domainAction === 'transfer' ? 'existing' : data.domainAction,
  );
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [availability, setAvailability] = useState<DomainAvailability | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkTimeout, setCheckTimeout] = useState<NodeJS.Timeout | null>(null);

  const validateDomain = (domain: string) => {
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    return domainRegex.test(domain);
  };

  const checkDomainAvailability = async (domain: string) => {
    if (!validateDomain(domain) || domainAction !== 'register') {
      setAvailability(null);
      return;
    }

    setChecking(true);
    try {
      const response = await domainService.checkDomainAvailability(domain);
      if (response.success) {
        setAvailability(response.data);
      } else {
        setAvailability(null);
      }
    } catch (error) {
      console.error('Error checking domain availability:', error);
      setAvailability(null);
    } finally {
      setChecking(false);
    }
  };

  // Debounce domain availability check
  useEffect(() => {
    if (checkTimeout) {
      clearTimeout(checkTimeout);
    }

    if (domainName && domainAction === 'register') {
      const timeout = setTimeout(() => {
        checkDomainAvailability(domainName.toLowerCase().trim());
      }, 500);
      setCheckTimeout(timeout);
    } else {
      setAvailability(null);
    }

    return () => {
      if (checkTimeout) {
        clearTimeout(checkTimeout);
      }
    };
  }, [domainName, domainAction]);

  const handleNext = () => {
    const newErrors: {[key: string]: string} = {};

    if (!domainName.trim()) {
      newErrors.domainName = 'Domain name is required';
    } else if (!validateDomain(domainName)) {
      newErrors.domainName = 'Please enter a valid domain name (e.g., example.com)';
    } else if (domainAction === 'register' && availability && !availability.available) {
      newErrors.domainName = 'This domain is not available for registration';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Domain price by action (server contract):
      //   register → price from the availability check (server recomputes
      //              from AWS × markup and allows ±$0.01)
      //   transfer → 12.99 (hosting only)
      //   existing → 0
      let domainPrice = 0;
      if (domainAction === 'register') {
        domainPrice = num(availability?.price) || 12.99;
      } else if (domainAction === 'transfer' && !isEmail) {
        domainPrice = 12.99;
      }

      // PlanStep recomputes the full total once a billing cycle is chosen;
      // here we only keep the running figure consistent (guarded — the
      // service price may not be set yet on this step).
      const prev: any = data.pricing || {};
      const servicePrice = num(prev.servicePrice) || num(prev.hostingPrice) || num(prev.emailPrice);
      const newTotal = Math.round((servicePrice + domainPrice) * 100) / 100;

      const pricing: any = { ...prev, domainPrice, total: newTotal };
      onNext({
        domainName: domainName.toLowerCase().trim(),
        domainAction,
        pricing,
      });
    }
  };

  return (
    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
      {/* Glossy overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
      
      <div className="max-w-2xl mx-auto relative z-10">
        {/* Badge */}
        <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
          <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
          <span className="text-sm font-semibold text-gray-700">Step 1: Domain Selection</span>
        </div>
        
        <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
          Choose Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Domain</span>
        </h2>
        
        <div className="space-y-6">
          {/* Domain Action Selection */}
          <div>
            <label className="block text-lg font-semibold text-gray-900 mb-4">
              What would you like to do with your domain?
            </label>
            <div className="space-y-4">
              <label className={`flex items-start p-6 bg-gradient-to-br from-white via-gray-50 to-gray-100 border-2 rounded-xl cursor-pointer transition-all duration-200 relative overflow-hidden ${
                domainAction === 'register' 
                  ? 'border-orange-400 ring-4 ring-orange-100 shadow-lg' 
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
              }`}>
                <input
                  type="radio"
                  name="domainAction"
                  value="register"
                  checked={domainAction === 'register'}
                  onChange={(e) => setDomainAction(e.target.value as 'register')}
                  className="h-5 w-5 text-orange-500 focus:ring-orange-500 border-gray-300 mt-1"
                />
                <div className="ml-4 flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-gray-900 text-lg">Register a new domain</div>
                    <span className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      $12.99/year
                    </span>
                  </div>
                  <div className="text-gray-600 mt-1">We'll register a new domain for you</div>
                </div>
              </label>
              
              {!isEmail && (
              <label className={`flex items-start p-6 bg-gradient-to-br from-white via-gray-50 to-gray-100 border-2 rounded-xl cursor-pointer transition-all duration-200 relative overflow-hidden ${
                domainAction === 'transfer' 
                  ? 'border-orange-400 ring-4 ring-orange-100 shadow-lg' 
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
              }`}>
                <input
                  type="radio"
                  name="domainAction"
                  value="transfer"
                  checked={domainAction === 'transfer'}
                  onChange={(e) => setDomainAction(e.target.value as 'transfer')}
                  className="h-5 w-5 text-orange-500 focus:ring-orange-500 border-gray-300 mt-1"
                />
                <div className="ml-4 flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-gray-900 text-lg">Transfer existing domain</div>
                    <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      $12.99 + 1 Year
                    </span>
                  </div>
                  <div className="text-gray-600 mt-1">Move your domain from another registrar (includes 1 year extension)</div>
                </div>
              </label>
              )}
              
              <label className={`flex items-start p-6 bg-gradient-to-br from-white via-gray-50 to-gray-100 border-2 rounded-xl cursor-pointer transition-all duration-200 relative overflow-hidden ${
                domainAction === 'existing' 
                  ? 'border-orange-400 ring-4 ring-orange-100 shadow-lg' 
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
              }`}>
                <input
                  type="radio"
                  name="domainAction"
                  value="existing"
                  checked={domainAction === 'existing'}
                  onChange={(e) => setDomainAction(e.target.value as 'existing')}
                  className="h-5 w-5 text-orange-500 focus:ring-orange-500 border-gray-300 mt-1"
                />
                <div className="ml-4 flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-gray-900 text-lg">{isEmail ? 'I already own this domain' : 'Point existing domain to 12and22'}</div>
                    <span className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Free
                    </span>
                  </div>
                  <div className="text-gray-600 mt-1">
                    {isEmail
                      ? 'Keep your registrar — we\'ll give you a few DNS records to add after checkout.'
                      : 'Use your own domain and point it to our hosting servers'}
                  </div>
                </div>
              </label>
            </div>
            {isEmail && (
              <p className="mt-3 text-sm text-gray-600" role="note">
                Domain transfer isn't available for Business Email yet — use 'I already own this domain' and keep your registrar.
              </p>
            )}
          </div>

          {/* Domain Name Input */}
          <div>
            <label htmlFor="domainName" className="block text-lg font-semibold text-gray-900 mb-3">
              Enter Your Domain Name
            </label>
            <div className="relative">
              <input
                type="text"
                id="domainName"
                value={domainName}
                onChange={(e) => setDomainName(e.target.value)}
                placeholder="example.com"
                aria-invalid={!!errors.domainName}
                aria-describedby="domainName-help"
                className={`block w-full px-6 py-4 pr-12 text-lg border-2 rounded-xl shadow-sm focus:outline-none focus:ring-4 focus:ring-orange-100 focus:border-orange-400 text-gray-900 placeholder-gray-400 transition-all duration-200 ${
                  errors.domainName ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                }`}
              />
              
              {/* Availability Status */}
              {domainAction === 'register' && domainName && validateDomain(domainName) && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {checking ? (
                    <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : availability ? (
                    availability.available ? (
                      <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )
                  ) : null}
                </div>
              )}
              
              {errors.domainName && (
                <p className="mt-1 text-sm text-red-600" role="alert">{errors.domainName}</p>
              )}
            </div>

            {/* Availability Message */}
            {domainAction === 'register' && availability && (
              <div className={`mt-3 p-4 rounded-xl ${
                availability.available 
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200' 
                  : 'bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200'
              }`}>
                <div className="flex items-center">
                  {availability.available ? (
                    <>
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                        <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold text-green-900">Great! {availability.domainName} is available</div>
                        <div className="text-sm text-green-700">
                          Registration fee: ${availability.price?.toFixed(2)}/year
                          {availability.premium && <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">Premium Domain</span>}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-3">
                        <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold text-red-900">Sorry, {availability.domainName} is not available</div>
                        <div className="text-sm text-red-700">Try a different name or extension</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            <p id="domainName-help" className="mt-3 text-sm text-gray-600 flex items-center">
              <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Enter your domain name without www (e.g., example.com)
            </p>
          </div>

          {/* Domain Features */}
          <div className="bg-gradient-to-br from-white via-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6 shadow-md relative overflow-hidden">
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent pointer-events-none rounded-xl"></div>
            <div className="relative z-10">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3 shadow-md relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-lg"></div>
                  <svg className="w-5 h-5 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-900 text-lg">Included with Your Plan</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(isEmail ? [
                  { icon: '📧', text: 'Mailboxes on your own domain' },
                  { icon: '🛡️', text: 'SPF, DKIM and DMARC records provided' },
                  { icon: '🔁', text: 'Aliases and catch-all forwarding' },
                  { icon: '🌐', text: 'Webmail, IMAP, SMTP and POP3' }
                ] : [
                  { icon: '🌐', text: 'Advanced DNS Management' },
                  { icon: '📧', text: 'Email forwarding setup' },
                  { icon: '🔒', text: 'SSL certificate installation' },
                  { icon: '🔗', text: 'Unlimited subdomain creation' }
                ]).map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="text-lg">{feature.icon}</span>
                    <span className="text-sm font-medium text-gray-700">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <button
            onClick={onBack}
            className="border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 shadow-md"
          >
            Back to Packages
          </button>
          <button
            onClick={handleNext}
            className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-lg font-semibold hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
            <span className="relative z-10">Continue to Billing</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DomainStep;